import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Loader2, Save, User as UserIcon, Mail, Shield, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getInitials = (name) => {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const roleColors = {
  owner: 'bg-gold/20 text-gold border-gold',
  admin: 'bg-red-100 text-red-800',
  sales_manager: 'bg-purple-100 text-purple-800',
  account_executive: 'bg-blue-100 text-blue-800',
  finance: 'bg-green-100 text-green-800',
  top_management: 'bg-amber-100 text-amber-800',
  pending: 'bg-slate-100 text-slate-600',
};

const roleLabel = (role) => ({
  owner: 'Owner',
  admin: 'Admin',
  sales_manager: 'Sales Manager',
  account_executive: 'Account Executive',
  finance: 'Finance',
  top_management: 'Top Management',
  pending: 'Pending',
}[role] || role);

export default function ProfilePage() {
  const { refreshPermissions } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', job_title: '', bio: '' });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) {
            setMe(d);
            setForm({
              name: d.name || '',
              phone: d.phone || '',
              job_title: d.job_title || '',
              bio: d.bio || '',
            });
          }
        }
      } catch (err) {
        logger.error('Profile load error', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!form.name || form.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/auth/me`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          job_title: form.job_title.trim(),
          bio: form.bio.trim(),
        }),
      });
      if (r.ok) {
        const d = await r.json();
        setMe(d);
        setDirty(false);
        toast.success('Profile updated');
        // Best-effort refresh of permissions context so header name updates
        try { refreshPermissions && refreshPermissions(); } catch (e) { /* ignore */ }
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save profile');
      }
    } catch (err) {
      logger.error('Profile save error', err);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!me) return;
    setForm({
      name: me.name || '',
      phone: me.phone || '',
      job_title: me.job_title || '',
      bio: me.bio || '',
    });
    setDirty(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!me) {
    return <div className="p-6 text-slate-500">Unable to load profile.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900">My Profile</h1>
        <p className="text-sm text-slate-500 mt-1">
          Update your name, phone, job title and bio. Email and role can only be changed by the Owner.
        </p>
      </div>

      {/* Identity card */}
      <Card data-testid="profile-identity-card">
        <CardHeader>
          <CardTitle className="text-base">Account Identity</CardTitle>
          <CardDescription>Linked to your Google Sign-In. These fields are read-only.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-4">
          <Avatar className="h-16 w-16 border-2 border-slate-200">
            <AvatarImage src={me.picture} alt={me.name} />
            <AvatarFallback className="bg-amber-100 text-amber-700 text-xl font-semibold">
              {getInitials(form.name || me.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Mail className="w-4 h-4" />
              <span data-testid="profile-email">{me.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Shield className="w-4 h-4 text-slate-500" />
              <Badge className={roleColors[me.role] || 'bg-slate-100'} data-testid="profile-role-badge">
                {roleLabel(me.role)}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Editable fields */}
      <Card data-testid="profile-edit-card">
        <CardHeader>
          <CardTitle className="text-base">Personal Info</CardTitle>
          <CardDescription>Only you can see and edit these fields (except Owner/Admin).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="profile-name" className="text-sm">Full name</Label>
              <Input
                id="profile-name"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Your full name"
                maxLength={120}
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <Label htmlFor="profile-phone" className="text-sm">Phone</Label>
              <Input
                id="profile-phone"
                value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                placeholder="+1 555 555 5555"
                maxLength={40}
                data-testid="profile-phone-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="profile-job-title" className="text-sm">Job title</Label>
            <Input
              id="profile-job-title"
              value={form.job_title}
              onChange={(e) => updateField('job_title', e.target.value)}
              placeholder="e.g. Senior Account Executive"
              maxLength={120}
              data-testid="profile-job-title-input"
            />
          </div>
          <div>
            <Label htmlFor="profile-bio" className="text-sm">
              Bio <span className="text-slate-400">({form.bio.length}/2000)</span>
            </Label>
            <Textarea
              id="profile-bio"
              value={form.bio}
              onChange={(e) => updateField('bio', e.target.value)}
              placeholder="Tell your team a bit about yourself..."
              rows={4}
              maxLength={2000}
              data-testid="profile-bio-input"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              To change your email address or role, contact your account Owner.
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!dirty || saving}
                data-testid="profile-reset-btn"
              >
                Reset
              </Button>
              <Button
                onClick={handleSave}
                disabled={!dirty || saving}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="profile-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save changes
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
